export class TransactionFileUploadResultModel {
  public constructor(
    public importFileId: string,
    public errorMessage: string | null = null,
    public errorDetailMessages: string[] | null = null
  ) {}

  public get isValid(): boolean {
    return !this.errorMessage;
  }
}

export interface ImportFileValidationResponse {
  importFileId: string;
  originalFileName: string;
  reportFileName: string;
  legalEntityName: string | null;
  legalEntityId: string | null;
  templateName: string | null;
  templateEntityId: string | null;
  errorMessages: string[] | null;
  warningMessages: string[] | null;
  infoMessages: string[] | null;
  isValidForSubmission: boolean;
}

export interface ImportFileReviewResultDto {
  isSuccess: boolean;
  isConflict: boolean;
  operationInProgress: ImportFileOperationInProgress;
  message: string | null;
  errorDetailMessages: string[] | null;
}

export enum ImportFileOperationInProgress {
  None = 0,
  Validation = 1,
  Approval = 2,
}

export interface ImportFileStatusResultDto {
  importFileId: string;
  processingStatus: ImportFileProcessingStatus;
  businessStatus: ImportFileBusinessStatus;
  validationDone: boolean;
  approvalDone: boolean;
  validationInProgress: boolean;
  approvalInProgress: boolean;
}

export enum BackgroundActionType {
  Validation,
  Approval,
}

export interface ImportFileSummaryResponse {
  importFileId: string;
  processingStatus: ImportFileProcessingStatus;
  businessStatus: ImportFileBusinessStatus;
  activeProcess: ImportFileActiveProcess;
  legalEntity: string | null;
  loadedOn: string | null;
  loadedBy: string | null;
  sourceFileName: string | null;
  validationReportFileName: string | null;
  templateName: string | null;
  lineStats: ImportFileLineStats | null;
  periods: ImportFilePeriods[] | null;
  rangeStart: string | null;
  rangeEnd: string | null;
  lastValidationAt: string | null;
  validatedBy: string | null;
}

export interface ImportFilePeriods {
  from: string;
  to: string;
  recordsCount: number;
}

export interface ImportFileDataImportsListItem {
  importFileId: string;
  importFileName: string;
  legalEntityId: string;
  legalEntityName: string;
  templateName: string;
  monthPeriods: string[];
  dataDateFrom: string;
  dataDateTo: string;
  businessStatus: ImportFileBusinessStatus;
  businessStatusName: string;
  lastUpdated: string;
  createdDate: string;
  uploadedById: string | null;
  uploadedBy: string;
  actionToTake: ImportFileActionToTake;
}

export interface ImportFileUploadedByFilter {
  id: string;
  fullName: string;
}

export interface ImportFileLineStats {
  total: number;
  correct: number;
  withWarnings: number;
  duplicates: number;
}

export enum ImportFileProcessingStatus {
  None = 0,
  Uploaded = 1,
  ValidationInProgress = 2,
  ValidationFailed = 3,
  ValidationSucceeded = 4,
  ApprovalStarted = 5,
  ApprovalInProgress = 6,
  ApprovalFailed = 7,
  ApprovalSucceeded = 8,
}

export enum ImportFileBusinessStatus {
  None = 0,
  Submitted = 1,
  ApprovalRequested = 2,
  Approved = 3,
  Rejected = 4,
}

export enum ImportFileActiveProcess {
  None = 0,
  Validation = 1,
  Approval = 2,
}

export function getImportFileBusinessStatusName(status: ImportFileBusinessStatus | null): string {
  switch (status) {
    case ImportFileBusinessStatus.None:
      return 'data-imports.summary.import-file-business-status.none';
    case ImportFileBusinessStatus.Submitted:
      return 'data-imports.summary.import-file-business-status.submitted';
    case ImportFileBusinessStatus.ApprovalRequested:
      return 'data-imports.summary.import-file-business-status.approval-requested';
    case ImportFileBusinessStatus.Approved:
      return 'data-imports.summary.import-file-business-status.approved';
    case ImportFileBusinessStatus.Rejected:
      return 'data-imports.summary.import-file-business-status.rejected';
    default:
      return 'data-imports.summary.import-file-business-status.none';
  }
}

export enum ImportFileActionToTake {
  View = 0,
  Review = 1,
}

export function getImportFileActionToTakeName(action: ImportFileActionToTake | null): string {
  switch (action) {
    case ImportFileActionToTake.View:
      return 'data-imports.grid.action-to-take.view';
    case ImportFileActionToTake.Review:
      return 'data-imports.grid.action-to-take.review';
    default:
      return '';
  }
}

export function getImportFileProcessingStatusName(status: ImportFileProcessingStatus | null): string {
  switch (status) {
    case ImportFileProcessingStatus.None:
      return 'data-imports.summary.import-file-processing-status.none';
    case ImportFileProcessingStatus.Uploaded:
      return 'data-imports.summary.import-file-processing-status.uploaded';
    case ImportFileProcessingStatus.ValidationInProgress:
      return 'data-imports.summary.import-file-processing-status.validation-in-progress';
    case ImportFileProcessingStatus.ValidationFailed:
      return 'data-imports.summary.import-file-processing-status.validation-failed';
    case ImportFileProcessingStatus.ValidationSucceeded:
      return 'data-imports.summary.import-file-processing-status.validation-succeeded';
    case ImportFileProcessingStatus.ApprovalStarted:
      return 'data-imports.summary.import-file-processing-status.approval-started';
    case ImportFileProcessingStatus.ApprovalInProgress:
      return 'data-imports.summary.import-file-processing-status.approval-in-progress';
    case ImportFileProcessingStatus.ApprovalFailed:
      return 'data-imports.summary.import-file-processing-status.approval-failed';
    case ImportFileProcessingStatus.ApprovalSucceeded:
      return 'data-imports.summary.import-file-processing-status.approval-succeeded';
    default:
      return 'data-imports.summary.import-file-processing-status.none';
  }
}
